import { CHARACTERS, QUESTIONS, TIEBREAK_QUESTION_IDS } from '../data/characterQuiz.js';

/**
 * Scoring the character quiz.
 *
 * Add up every option's points, multiplied by its question weight, and the
 * highest total wins. The interesting part is what happens when two are level,
 * which on a twelve-way result with ten questions is not rare.
 *
 * ── Breaking ties ───────────────────────────────────────────────────────────
 *
 * Taking the first alphabetically, or whichever the object happened to list
 * first, would be invisible to us and obvious to a player: two people answering
 * almost identically would get the same figure for no reason connected to their
 * answers.
 *
 * So a tie is broken on questions 4, 8 and 10 — the three that ask where
 * somebody is right now rather than what they are like in general. Their
 * relationship with God, the season they are in, one word for their faith. A
 * result that contradicts those three reads as wrong however well the other
 * seven fit, so those three are also what decides a close call, in that order.
 *
 * If they are still level after all three, the winner is the one that scored on
 * more questions — a character supported across the whole quiz beats one that
 * spiked twice — and only then does it fall back to declaration order, which by
 * that point is genuinely arbitrary and rare enough not to matter.
 */

/** Every character at zero, so nothing has to check for undefined. */
const emptyTally = () => Object.fromEntries(Object.keys(CHARACTERS).map((id) => [id, 0]));

/**
 * @param {Array<number|null>} answers one option index per question, in order
 * @returns {{ id, character, scores, ranked, answered, complete }}
 */
export function scoreQuiz(answers) {
  const scores = emptyTally();
  // How many questions each character scored anything on. Used as the last
  // meaningful tie-break before arbitrary ordering.
  const breadth = emptyTally();

  QUESTIONS.forEach((question, i) => {
    const choice = answers[i];
    const option = question.options[choice];
    if (!option) return;
    const weight = question.weight ?? 1;
    for (const [id, points] of Object.entries(option.scores)) {
      if (!(id in scores)) continue; // a typo in the data must not create a character
      scores[id] += points * weight;
      breadth[id] += 1;
    }
  });

  // Points from the three "where are you now" questions alone, per character.
  const nowScores = TIEBREAK_QUESTION_IDS.map((qid) => {
    const i = QUESTIONS.findIndex((q) => q.id === qid);
    const option = QUESTIONS[i]?.options[answers[i]];
    const tally = emptyTally();
    if (option) {
      for (const [id, points] of Object.entries(option.scores)) {
        if (id in tally) tally[id] = points;
      }
    }
    return tally;
  });

  const order = Object.keys(CHARACTERS);
  const ranked = order
    .map((id) => ({ id, score: scores[id] }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      for (const tally of nowScores) {
        if (tally[b.id] !== tally[a.id]) return tally[b.id] - tally[a.id];
      }
      if (breadth[b.id] !== breadth[a.id]) return breadth[b.id] - breadth[a.id];
      return order.indexOf(a.id) - order.indexOf(b.id);
    });

  const answered = answers.filter((a) => a !== null && a !== undefined).length;

  return {
    id: ranked[0].id,
    character: CHARACTERS[ranked[0].id],
    scores,
    ranked,
    answered,
    complete: answered === QUESTIONS.length,
  };
}

/**
 * The runner-up, when it is close enough to be worth naming.
 *
 * Shown as "there is a lot of X in you too" on the result. It is the line that
 * makes a result feel read rather than assigned, and it is deliberately not
 * shown when the gap is wide — pointing at a distant second would undercut the
 * first rather than enrich it.
 */
export function alsoLike(result) {
  const [first, second] = result.ranked;
  if (!second || first.score === 0) return null;
  const gap = (first.score - second.score) / first.score;
  return gap <= 0.18 ? CHARACTERS[second.id] : null;
}

/** What gets copied when someone shares the result as text. */
export function shareText(character, url) {
  return (
    `I got ${character.name} ${character.emoji} — ${character.tagline.toLowerCase()}.\n\n` +
    `"${character.verse.text}" — ${character.verse.ref}\n\n` +
    `Which Bible character are you? ${url}`
  );
}
